import { useMutation, useQuery } from "@apollo/client";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import ErrorBoundary from "@/components/ErrorBoundary";
import SectionHeading from "@/components/SectionHeading";
import usePageTitle from "@/hooks/usePageTitle";
import { NotificationEmailList, type NotificationEmailField } from "./_components/NotificationEmailList";
import { UPDATE_SECTION_NOTIFICATION_EMAILS_MUTATION } from "./mutations";
import { emailFieldError, emailsForInputs, joinEmailList, splitEmailList } from "./notificationEmails";
import { SECTION_NOTIFICATION_EMAILS_QUERY } from "./queries";
import { useSectionOperationsAccess } from "./useSectionOperationsAccess";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

const CHANGE_FORM_REQUIRED_ERROR = "Enter at least one email.";
const UNSAVED_CHANGES_MESSAGE = "Your changes will not be saved until you click Save.";

function fieldsFromStored(value: string | null | undefined, nextId: { current: number }): NotificationEmailField[] {
  return emailsForInputs(value).map((email) => ({
    id: `email-${nextId.current++}`,
    value: email
  }));
}

function fieldValues(fields: NotificationEmailField[]): string[] {
  return fields.map((field) => field.value);
}

export default function SectionOperationsNotificationsPage() {
  const { sectionId } = useParams();
  const { ready, showNotifications, showCommittees, showSectionOfficeFacilityTabs } =
    useSectionOperationsAccess(sectionId);
  const { data, loading } = useQuery(SECTION_NOTIFICATION_EMAILS_QUERY, {
    variables: { sectionId: sectionId ?? "" },
    skip: !sectionId || !showNotifications
  });
  const [saveEmails, { loading: saving }] = useMutation(UPDATE_SECTION_NOTIFICATION_EMAILS_MUTATION);

  const nextFieldId = useRef(0);
  const isDirtyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);
  const [contactEmails, setContactEmails] = useState<NotificationEmailField[]>(() => fieldsFromStored("", nextFieldId));
  const [notificationsEmails, setNotificationsEmails] = useState<NotificationEmailField[]>(() =>
    fieldsFromStored("", nextFieldId)
  );
  const [contactErrors, setContactErrors] = useState<Array<string | undefined>>([]);
  const [notificationsErrors, setNotificationsErrors] = useState<Array<string | undefined>>([]);

  const sectionRecordId = data?.section?.id;
  const contactEmail = data?.section?.contactEmail;
  const notificationsEmail = data?.section?.notificationsEmail;

  usePageTitle(`/sections/${sectionId}/operations/notifications`, "Sections | Operations | Notifications");

  function markDirty() {
    isDirtyRef.current = true;
    setIsDirty(true);
  }

  function clearDirty() {
    isDirtyRef.current = false;
    setIsDirty(false);
  }

  useEffect(() => {
    clearDirty();
  }, [sectionRecordId]);

  useEffect(() => {
    if (!sectionRecordId || isDirtyRef.current) return;
    setContactEmails(fieldsFromStored(contactEmail, nextFieldId));
    setNotificationsEmails(fieldsFromStored(notificationsEmail, nextFieldId));
    setContactErrors([]);
    setNotificationsErrors([]);
  }, [sectionRecordId, contactEmail, notificationsEmail]);

  if (!sectionId || !ready) {
    return null;
  }

  if (!showNotifications) {
    return (
      <Navigate
        to={
          showCommittees
            ? `/sections/${sectionId}/operations/committees`
            : showSectionOfficeFacilityTabs
              ? `/sections/${sectionId}/operations/employees`
              : `/sections/${sectionId}/jobs`
        }
        replace
      />
    );
  }

  if (loading && !data) {
    return null;
  }

  if (!data?.section) {
    return (
      <div className="w-full" data-testid="section-operations-notifications">
        <ErrorBoundary
          variant="inline"
          title="Failed to Load Notifications"
          message="We couldn't load notification emails for this section. Please try again or contact tech support."
          className="mt-12"
        />
      </div>
    );
  }

  const canEdit = data.section.permissions?.manageNotificationEmails === true;

  function validate(): boolean {
    const contactValues = fieldValues(contactEmails);
    const notificationsValues = fieldValues(notificationsEmails);
    const nextContactErrors: Array<string | undefined> = contactValues.map(emailFieldError);
    const nextNotificationsErrors: Array<string | undefined> = notificationsValues.map(emailFieldError);

    if (splitEmailList(joinEmailList(contactValues)).length === 0) {
      const filledErrors = [...nextContactErrors];
      filledErrors[0] = filledErrors[0] ?? CHANGE_FORM_REQUIRED_ERROR;
      setContactErrors(filledErrors);
      setNotificationsErrors(nextNotificationsErrors);
      return false;
    }

    setContactErrors(nextContactErrors);
    setNotificationsErrors(nextNotificationsErrors);
    return nextContactErrors.every((message) => !message) && nextNotificationsErrors.every((message) => !message);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canEdit || !sectionId || !isDirty) return;
    if (!validate()) return;

    try {
      const { data: result } = await saveEmails({
        variables: {
          input: {
            sectionId,
            contactEmail: joinEmailList(fieldValues(contactEmails)),
            notificationsEmail: joinEmailList(fieldValues(notificationsEmails))
          }
        }
      });

      const payload = result?.updateSectionNotificationEmails;
      const { success = false, message = "Something went wrong" } = payload ?? {};
      if (!success) {
        showErrorSnackbar("Notification emails not updated", { subtitle: message });
        return;
      }

      clearDirty();
      nextFieldId.current = 0;
      const savedSection = payload?.section;
      setContactEmails(
        fieldsFromStored(savedSection?.contactEmail ?? joinEmailList(fieldValues(contactEmails)), nextFieldId)
      );
      setNotificationsEmails(
        fieldsFromStored(
          savedSection?.notificationsEmail ?? joinEmailList(fieldValues(notificationsEmails)),
          nextFieldId
        )
      );
      setContactErrors([]);
      setNotificationsErrors([]);
      showSnackbar("Notification emails updated");
    } catch {
      showErrorSnackbar("Notification emails not updated", { subtitle: "Please try again later." });
    }
  }

  return (
    <div className="w-full" data-testid="section-operations-notifications">
      <form onSubmit={handleSubmit}>
        {canEdit && isDirty && (
          <Banner
            className="mt-8"
            variant="warn"
            description={UNSAVED_CHANGES_MESSAGE}
            hideSecondaryDismiss
            testId="unsaved-notification-emails"
          />
        )}
        <SectionHeading
          heading="Employment (Change Form) and Facility Updates"
          body="Sent for employment change requests and facility updates."
        />
        <NotificationEmailList
          emails={contactEmails}
          canEdit={canEdit}
          disabled={saving}
          addTestId="add-contact-email"
          fieldTestIdPrefix="contact-email"
          fieldLabel="Email"
          addLabel="Add Email"
          removeAriaLabel="Remove Email"
          errors={contactErrors}
          onChange={(next) => {
            markDirty();
            setContactEmails(next);
            setContactErrors([]);
          }}
        />
        <SectionHeading
          heading="PAT Notifications"
          body="Sent when PAT results or payouts are due, a registration is cancelled late, a PAT suspension is issued, or a Weather Event is recorded."
        />
        <NotificationEmailList
          emails={notificationsEmails}
          canEdit={canEdit}
          disabled={saving}
          addTestId="add-notifications-email"
          fieldTestIdPrefix="notifications-email"
          fieldLabel="PAT Notification Email"
          addLabel="Add PAT Notification Email"
          removeAriaLabel="Remove PAT Notification Email"
          errors={notificationsErrors}
          onChange={(next) => {
            markDirty();
            setNotificationsEmails(next);
            setNotificationsErrors([]);
          }}
        />
        {canEdit && (
          <div className="mt-6">
            <Button
              type="submit"
              variant="filled"
              loading={saving}
              disabled={!isDirty}
              testId="save-notification-emails"
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
